/**
 * SolarUtility stores one numeric field per calendar month (localized long name, e.g. "January").
 * Those values are treated as average daily kWh for that month.
 * Annual kWh = Σ (daily_kWh × days in that month).
 *
 * Pass the same month-long labels you use when reading/writing `su` (match `UtilityDetailsCard` / API).
 */
export function daysInMonth(year: number, monthIndex0: number): number {
    return new Date(year, monthIndex0 + 1, 0).getDate();
}

/** Annual kWh from SolarUtility long month keys (daily rates). */
/** Long month names in locale default order (matches Utility Details / ThreeDViewer). */
export function solarUtilityMonthLongLabelsDefault(): string[] {
    return Array.from({ length: 12 }, (_, i) =>
        new Date(0, i).toLocaleString('default', { month: 'long' }),
    );
}

/**
 * Annual usage for sellable checks, production offsets, and oneBUTTON Save Design.
 * oneDRAW-only: Utility Details / Salesforce via {@link effectiveAnnualUsageKwhForPricing}.
 * Show_OneButton (Save Design annual usage field): viewer store `annualUsage` override when set.
 */
export function resolveEffectiveAnnualUsageKwh(options: {
    useOneButtonAnnualInput: boolean;
    oneButtonAnnualUsageOverride: number | null | undefined;
    solarUtility: Record<string, unknown> | undefined | null;
    sfAnnualUsage: number | null | undefined;
    monthLongNames?: readonly string[];
}): number | null {
    const {
        useOneButtonAnnualInput,
        oneButtonAnnualUsageOverride,
        solarUtility,
        sfAnnualUsage,
        monthLongNames,
    } = options;

    if (useOneButtonAnnualInput) {
        const override = oneButtonAnnualUsageOverride;
        if (typeof override === 'number' && Number.isFinite(override) && override > 0) {
            return Math.round(override);
        }
    }

    return effectiveAnnualUsageKwhForPricing(solarUtility, sfAnnualUsage, monthLongNames);
}

/**
 * Same resolution as ThreeDViewer `dashboardAnnualUsage`: Utility Details (monthly mode, explicit annual,
 * or sum of monthly daily kWh) then Salesforce annual load. Use for pricing API and financing header.
 */
export function effectiveAnnualUsageKwhForPricing(
    solarUtility: Record<string, unknown> | undefined | null,
    sfAnnualUsage: number | null | undefined,
    monthLongNames?: readonly string[],
): number | null {
    const months = monthLongNames?.length ? monthLongNames : solarUtilityMonthLongLabelsDefault();
    if (!solarUtility) {
        if (typeof sfAnnualUsage === 'number' && Number.isFinite(sfAnnualUsage) && sfAnnualUsage > 0) {
            return Math.round(sfAnnualUsage);
        }
        return null;
    }
    const derived = annualKwhFromMonthlyDailyFields(solarUtility, months);
    const monthlyMode = solarUtility.EnterAnnualUsage === 'MonthlyUsageCont';
    const annualRaw = solarUtility.AnnualUsage;
    const explicit =
        annualRaw != null && annualRaw !== '' && Number.isFinite(Number(annualRaw)) && Number(annualRaw) > 0
            ? Number(annualRaw)
            : null;
    let fromCard: number | null = null;
    if (monthlyMode && derived > 0) fromCard = derived;
    else if (explicit != null) fromCard = explicit;
    else if (derived > 0) fromCard = derived;

    if (fromCard != null && fromCard > 0) return Math.round(fromCard);
    if (typeof sfAnnualUsage === 'number' && Number.isFinite(sfAnnualUsage) && sfAnnualUsage > 0) {
        return Math.round(sfAnnualUsage);
    }
    return null;
}

export function annualKwhFromMonthlyDailyFields(
    su: Record<string, unknown> | undefined | null,
    monthLongNames: readonly string[],
    year: number = new Date().getFullYear(),
): number {
    if (!su || monthLongNames.length === 0) return 0;
    let total = 0;
    for (let i = 0; i < Math.min(12, monthLongNames.length); i++) {
        const key = monthLongNames[i];
        const daily = Number(su[key]) || 0;
        if (!Number.isFinite(daily)) continue;
        total += daily * daysInMonth(year, i);
    }
    return Math.round(total);
}

/** Map row month to 0–11: 1–12, "Jan"/"January", etc. */
function resolveMonthIndex0(month: unknown, year: number): number | null {
    if (month == null || month === '') return null;
    if (typeof month === 'number' && Number.isFinite(month)) {
        const n = Math.round(month);
        if (n >= 1 && n <= 12) return n - 1;
        return null;
    }
    const s = String(month).trim();
    if (!s) return null;
    if (/^\d{1,2}$/.test(s)) {
        const n = parseInt(s, 10);
        if (n >= 1 && n <= 12) return n - 1;
    }
    const t = `${s} 1, ${year}`;
    const d = new Date(t);
    if (Number.isNaN(d.getTime())) return null;
    return d.getMonth();
}

/** Annual kWh from Monthly usage tab `Data` rows (`MonthUsage` = daily kWh per month). */
export function annualKwhFromMonthlyUsageData(
    data: Array<{ Month?: string | number; MonthUsage?: number | string }> | undefined | null,
    year: number = new Date().getFullYear(),
): number {
    if (!Array.isArray(data) || data.length === 0) return 0;
    let total = 0;
    for (const row of data) {
        const idx = resolveMonthIndex0(row.Month, year);
        if (idx == null) continue;
        const daily = Number(row.MonthUsage) || 0;
        if (!Number.isFinite(daily)) continue;
        total += daily * daysInMonth(year, idx);
    }
    return Math.round(total);
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * Map utility-bill reader rows (numeric month, PascalCase, etc.) to the same shape as the Monthly tab
 * so annual = Σ(daily × days in month) and save lookups (Jan, Feb, …) work.
 */
export function normalizeMonthlyUsageRowsFromBill(
    rows: Array<Record<string, unknown>> | null | undefined,
): Array<{ Month: string; MonthUsage: number; Amount: number }> {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((row) => {
        const m = row.month ?? row.Month;
        let monthShort = '';
        if (typeof m === 'number' && Number.isFinite(m)) {
            const n = Math.round(Number(m));
            if (n >= 1 && n <= 12) monthShort = MONTH_SHORT[n - 1];
        } else if (m != null && String(m).trim() !== '') {
            const s = String(m).trim();
            if (/^\d{1,2}$/.test(s)) {
                const n = parseInt(s, 10);
                if (n >= 1 && n <= 12) monthShort = MONTH_SHORT[n - 1];
            }
            if (!monthShort) {
                const d = new Date(`${s} 1, ${new Date().getFullYear()}`);
                if (!Number.isNaN(d.getTime())) monthShort = MONTH_SHORT[d.getMonth()];
            }
        }
        const rawVal = row.value ?? row.Value ?? 0;
        const rawAmt = row.amount ?? row.Amount ?? 0;
        return {
            Month: monthShort || (m != null ? String(m) : ''),
            MonthUsage: Number(rawVal) || 0,
            Amount: Number(rawAmt) || 0,
        };
    });

    
}

export function normalizeDailyUsageRowsFromBill(
    rows: Array<Record<string, unknown>> | null | undefined,year:number
): Array<{ Month: string; DailyUsage: number; MonthAmount: number,MonthUsage:number }> {
    let data: Array<{ Month: string; DailyUsage: number; MonthAmount: number,MonthUsage:number }> = [];

    if(!Array.isArray(rows) || rows.length === 0) return data;
    rows.forEach((row) => {
        const m:any = row.month ?? row.Month;
        const du = row.value ??  0;
        const daysInMonth = getDaysInMonth(m, year);
        const mu:any = row.value ? (Number(row.value) * Number(daysInMonth)).toFixed(2) : 0;
        const ma = 0;
        data.push({
            Month: m,
            DailyUsage: Number(du) || 0,
            MonthAmount: Number(ma) || 0,
            MonthUsage: Number(mu) || 0,
        });
    });    
    const months:string[] = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    data.sort((a:any, b:any) => {
        return months.indexOf(a.Month) - months.indexOf(b.Month);
    });
    
    return data;
}


export const getDaysInMonth = (month: string, year: number) => {
    // Map month name to a zero-based index (January = 0, February = 1, etc.)
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Use indexOf to get the month index (0-11)
    const monthIndex = months.indexOf(month);

    // Check if the month name is valid
    if (monthIndex === -1) {
        return "Invalid month name";
    }

    // Create a new Date object for the first day of the *next* month
    // and set the day to 0, which results in the last day of the *current* month.
    // The month parameter in the Date constructor is 0-based, so we use monthIndex + 1.
    const date = new Date(year, monthIndex + 1, 0);

    // Use getDate() to return the day of the month, which is the total number of days
    // in the specified month.
    return date.getDate();
}


